import { ReactElement } from 'react';
import ReactBarcode from 'react-barcode';
import { GiHandTruck } from 'react-icons/gi';
import {
  MdLocalShipping,
  MdLocationOn,
  MdStoreMallDirectory,
} from 'react-icons/md';
import { useAuth } from 'hooks';
import { DeliveryModeData } from 'types';
import { Heading, Text } from 'components';
import { getDeliveryDisplay } from 'helpers/get-delivery-display';
import { FontSize, FontWeight } from 'commons';
import * as S from './styles';

interface Props {
  displayData: {
    brandLogo: ReactElement;
    consignmentCode: string;
    isPickupInStore: boolean;
    storeDisplay: {
      name: string;
      street: string;
      districtAndTown: string;
      postalCode: string;
      renderPhone: ReactElement | null;
    };
    customerDisplay: {
      name: string;
      street: string;
      districtAndTown: string;
      postalCode: string;
      renderPhone: ReactElement | null;
      complement: string;
    };
    deliveryMode: DeliveryModeData;
  };
}

export function ShippingLabelLarge({ displayData }: Props) {
  const { selectedStore } = useAuth();
  const store = selectedStore?.name || '';

  const {
    brandLogo,
    consignmentCode,
    isPickupInStore,
    storeDisplay,
    customerDisplay,
    deliveryMode,
  } = displayData;

  const deliveryDisplay = getDeliveryDisplay({ store, deliveryMode });

  function renderStore() {
    const { name, street, districtAndTown, postalCode, renderPhone } =
      storeDisplay;

    return (
      <S.InfoWrapper>
        <S.IconWrapper>
          <S.Icon>
            <GiHandTruck />
          </S.Icon>

          <Heading as="h3" size={FontSize.font16} weight={FontWeight.bold}>
            {name}
          </Heading>
        </S.IconWrapper>

        <S.TextWrapper>
          <Text>{street}</Text>
          <Text>{districtAndTown}</Text>
          <Text>{postalCode}</Text>
          {renderPhone}
        </S.TextWrapper>
      </S.InfoWrapper>
    );
  }

  function renderCustomer() {
    const {
      name,
      street,
      complement,
      districtAndTown,
      postalCode,
      renderPhone,
    } = customerDisplay;

    return (
      <S.Section>
        <S.InfoWrapper>
          <S.IconWrapper>
            <S.Icon>
              <MdLocationOn />
            </S.Icon>

            <Heading as="h3" size={FontSize.font16} weight={FontWeight.bold}>
              {name}
            </Heading>
          </S.IconWrapper>

          <S.TextWrapper>
            <Text>
              {street}
              {complement ? ` - ${complement}` : ''}
            </Text>
            <Text>{districtAndTown}</Text>
            <Text>{postalCode}</Text>
            {renderPhone}
          </S.TextWrapper>
        </S.InfoWrapper>
      </S.Section>
    );
  }

  return (
    <S.Main>
      <S.Wrapper>
        <S.Logo>{brandLogo}</S.Logo>

        {isPickupInStore ? (
          <S.Container>
            <MdStoreMallDirectory size={25} />

            <Text weight={FontWeight.bold}>Retire na loja</Text>
          </S.Container>
        ) : (
          <S.Container>
            <MdLocalShipping size={25} />

            <Text weight={FontWeight.bold}>{deliveryDisplay}</Text>
          </S.Container>
        )}
      </S.Wrapper>

      {renderStore()}

      {renderCustomer()}

      <S.Section>
        <ReactBarcode
          value={consignmentCode}
          height={50}
          textPosition="top"
          textAlign="center"
          fontOptions="bold"
          fontSize={12}
          font="arial"
          width={2}
          margin={0}
          marginLeft={3}
          marginRight={3}
        />
      </S.Section>
    </S.Main>
  );
}
